import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { apiGet } from '../core/data/restClient';
import ApiErrorSnackbar from './ApiErrorSnackbar';
import ErrorBoundary from './ErrorBoundary';
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from './Sidebar';
import CommandPalette from './CommandPalette';
import BottomTabBar, { BOTTOM_TAB_BAR_HEIGHT } from './BottomTabBar';
import MobileTopBar from './MobileTopBar';
import MoreDrawer from './MoreDrawer';
import { useResponsive } from '../hooks/useResponsive';
import { useAuth } from '../core/auth/useAuth';
import { useUnreadCount } from '../features/notifications/useNotifications';
import { useTimerContext } from '../features/kronos/timer/TimerContext';
import FloatingTimerBar from '../features/kronos/timer/FloatingTimerBar';
import MilestoneReminderDialog from '../features/projects/MilestoneReminderDialog';
import NewVersionNotice from './NewVersionNotice';

function getPageTitle(pathname: string, t: (k: string) => string): string {
  if (pathname.startsWith('/kronos/tracker')) return t('nav.tracker');
  if (pathname.startsWith('/kronos/timesheet')) return t('nav.timesheet');
  if (pathname.startsWith('/kronos/dashboard')) return t('nav.dashboard');
  if (pathname.startsWith('/kronos/report')) return t('nav.report');
  if (pathname.startsWith('/kronos/calendar')) return t('nav.calendar');
  if (pathname.startsWith('/projects')) return t('nav.projectsHub');
  if (pathname.startsWith('/customers')) return t('nav.customers');
  if (pathname.startsWith('/notifications')) return t('nav.notifications');
  if (pathname.startsWith('/settings')) return t('nav.settings');
  return 'Altyor Projects Hub';
}

function deriveInitials(displayName: string | undefined): string {
  if (!displayName) return 'U';
  const parts = displayName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase() || 'U';
}

export default function AppShell() {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const timer = useTimerContext();
  const { user, logout } = useAuth();
  const unreadCount = useUnreadCount(user?.uid);

  // Re-sync the sidebar collapsed state when the viewport crosses the
  // breakpoint — a manual toggle still overrides within the same regime.
  useEffect(() => {
    setCollapsed(isMobile);
  }, [isMobile]);

  // Global Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const closePalette = () => setPaletteOpen(false);

  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;
  const sidebarWidth = isMobile ? 0 : width;

  // Resolve project + designation labels for the floating bar
  const [floatingProjectLabel, setFloatingProjectLabel] = useState('');
  const [floatingDesignationLabel, setFloatingDesignationLabel] = useState('');

  useEffect(() => {
    if (!timer.state?.projectId) {
      setFloatingProjectLabel('');
      setFloatingDesignationLabel('');
      return;
    }
    const pid = timer.state.projectId;
    const did = timer.state.designationId;
    // Read the labels via /api (per-doc reads, open to any authenticated user).
    apiGet<{ code?: string; name?: string }>(`/projects/${pid}`)
      .then((d) => `${d.code} — ${d.name}`)
      .then(setFloatingProjectLabel)
      .catch(() => setFloatingProjectLabel(pid));
    if (did) {
      apiGet<{ name?: string }>(`/projects/${pid}/designations/${did}`)
        .then((d) => d.name ?? did)
        .then(setFloatingDesignationLabel)
        .catch(() => setFloatingDesignationLabel(did));
    }
  }, [timer.state?.projectId, timer.state?.designationId]);

  const floatingAutoStopLabel = timer.state?.autoStopAt
    ? t('kronos.timer.stopsAt', {
        time: new Date(timer.state.autoStopAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      })
    : null;

  const isOnTracker = location.pathname === '/kronos/tracker';
  const showFloating =
    !isOnTracker &&
    timer.state !== null &&
    (timer.state.status === 'running' || timer.state.status === 'paused') &&
    !timer.needsRecovery;

  const title = getPageTitle(location.pathname, t);
  const userInitials = deriveInitials(user?.displayName);
  const isAdmin = user?.roles.includes('admin') ?? false;

  // Bottom padding clears BottomTabBar + FloatingTimerBar on mobile.
  const bottomPadding = isMobile
    ? `calc(${BOTTOM_TAB_BAR_HEIGHT}px + ${showFloating ? 56 : 0}px + env(safe-area-inset-bottom))`
    : undefined;

  return (
    <Box sx={{ display: 'flex', overflow: 'hidden' }}>
      {!isMobile && (
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
          onOpenPalette={() => setPaletteOpen(true)}
        />
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: `${sidebarWidth}px`,
          minHeight: '100dvh',
          minWidth: 0,
          transition: 'margin-left 0.2s ease',
          pb: bottomPadding,
        }}
      >
        {isMobile && (
          <MobileTopBar
            title={title}
            unreadCount={unreadCount}
            onAvatarClick={() => setMoreOpen(true)}
            userInitials={userInitials}
            avatarSrc={user?.photoURL}
          />
        )}

        <Box sx={{ p: { xs: 2, sm: 3, md: 5 }, position: 'relative' }}>
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
          {/* Brand watermark */}
          <Box
            component="img"
            src="/images/brand-watermark.svg"
            alt=""
            sx={{
              position: 'fixed',
              bottom: -50,
              right: -170,
              width: 500,
              height: 815,
              objectFit: 'contain',
              opacity: 0.20,
              pointerEvents: 'none',
              zIndex: -1,
            }}
          />
        </Box>
      </Box>

      {isMobile && (
        <>
          <BottomTabBar
            onOpenSearch={() => setPaletteOpen(true)}
            onOpenMore={() => setMoreOpen(true)}
            unreadCount={unreadCount}
          />
          <MoreDrawer
            open={moreOpen}
            onClose={() => setMoreOpen(false)}
            onSignOut={() => {
              setMoreOpen(false);
              logout();
            }}
            onChangeLanguage={(lang) => i18n.changeLanguage(lang)}
            currentLanguage={(i18n.language === 'fr' ? 'fr' : 'en') as 'en' | 'fr'}
            isAdmin={isAdmin}
          />
        </>
      )}

      <ApiErrorSnackbar />

      <CommandPalette open={paletteOpen} onClose={closePalette} fullScreen={isMobile} />

      {/* Rappel des échéances dépassées — se monte à vide et ne fetche rien
          tant que l'utilisateur n'est pas chargé ou que le jour a déjà été vu. */}
      {user && <MilestoneReminderDialog />}

      <NewVersionNotice current={import.meta.env.VITE_APP_VERSION} />

      <FloatingTimerBar
        visible={showFloating}
        projectLabel={floatingProjectLabel}
        designationLabel={floatingDesignationLabel}
        autoStopLabel={floatingAutoStopLabel}
        elapsedMs={timer.elapsedMs}
        status={(timer.state?.status as 'running' | 'paused') ?? 'running'}
        onPause={timer.actions.pause}
        onResume={timer.actions.resume}
        onStop={timer.actions.stop}
        onNavigate={() => navigate('/kronos/tracker')}
      />
    </Box>
  );
}
